
import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogType?: string;
  schemas?: object[];
  schema?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  schemas,
  schema,
}: SEOProps) => {
  useEffect(() => {
    // Set the page title
    document.title = title;

    /**
     * Helper to create or update a meta tag.
     * @param name      - meta name or property value
     * @param content   - meta content
     * @param isProperty - true if the meta uses the `property` attribute (Open Graph/Twitter)
     */
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement | null;

      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }

      // Defensive check – ensure we have a valid element before setting content
      if (element) {
        element.setAttribute('content', content);
      }
    };

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:url', `https://www.slcontractorsofal.com${canonical}`, true);

    // Twitter tags
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);

    // Canonical link handling
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', `https://www.slcontractorsofal.com${canonical}`);

    // Remove any previously injected schema scripts
    document.querySelectorAll('script[data-seo-schema]').forEach((el) => el.remove());

    // Determine which schemas to render
    const allSchemas = schemas ?? (schema ? [schema] : []);

    // Inject new schema scripts
    allSchemas.forEach((s, i) => {
      const script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-seo-schema', `schema-${i}`);
      // Use a try/catch in case the object cannot be stringified
      try {
        script.textContent = JSON.stringify(s);
        document.head.appendChild(script);
      } catch (err) {
        console.error('Failed to serialize SEO schema', err);
      }
    });

    // Cleanup on unmount or when dependencies change
    return () => {
      document.querySelectorAll('script[data-seo-schema]').forEach((el) => el.remove());
    };
  }, [title, description, keywords, canonical, ogType, schemas, schema]);
};
